import { call, put, select, takeEvery } from 'redux-saga/effects'
import { handleApiError } from '../../../components/apiErrorHandler/store/apiErrorHandler.actions'
import { getAuthenticationJwtSelector } from '../../login/selectors/getAuthenticationJwt.selector'
import { EKGImportConstants } from '../store/ekgImport.constants'
import { EKGImportService } from '../services/ekgImport.service'
import {
  IEKGImportState,
  IFetchReportImageRequest,
  IFetchReportImageResponse,
  IReportAttachment,
} from '../store/ekgImport.types'
import { getEKGImportSelector } from '../selectors'
import { saveReportAttachments } from '../store/ekgImport.actions'
import { IMAGE_EXTENSIONS } from '../store/ekgImport.reducers'
import { isOffline } from '../../../utilities'

export function* getReportAttachmentImage() {
  const authenticationJwt: string = yield select(getAuthenticationJwtSelector)
  const { reportAttachments, loadingReportId }: IEKGImportState = yield select(getEKGImportSelector)

  const idToUpdate = loadingReportId
  if (idToUpdate === null) return

  const reportAttachmentToUpdateIndex = (reportAttachments as Array<IReportAttachment>).findIndex(
    (reportAttachment) => Number(reportAttachment.id) === idToUpdate
  )
  if (reportAttachmentToUpdateIndex === -1) return

  const reportAttachmentToUpdate = reportAttachments[reportAttachmentToUpdateIndex]
  if (reportAttachmentToUpdate?.data) return

  // If report attachment is not an image, do not continue
  const reportAttachmentFileExtension = reportAttachmentToUpdate.fileExtension.toLowerCase()
  if (!IMAGE_EXTENSIONS.includes(reportAttachmentFileExtension)) {
    const nonImageReportAttachment: IReportAttachment = {
      ...reportAttachmentToUpdate,
      isLoading: false,
      isUnavailable: true,
      data: '',
    }

    reportAttachments[reportAttachmentToUpdateIndex] = nonImageReportAttachment
    yield put(saveReportAttachments(reportAttachments))

    return
  }

  reportAttachments[reportAttachmentToUpdateIndex] = {
    ...reportAttachmentToUpdate,
    isLoading: true,
  }

  yield put(saveReportAttachments(reportAttachments))

  if (isOffline()) {
    const newReportAttachment: IReportAttachment = {
      ...reportAttachmentToUpdate,
      isLoading: false,
      isUnavailable: true,
      data: '',
    }

    reportAttachments[reportAttachmentToUpdateIndex] = newReportAttachment
    yield put(saveReportAttachments(reportAttachments))

    return
  }

  try {
    const ekgImportService = new EKGImportService(authenticationJwt as string)
    const fetchReportImageRequest: IFetchReportImageRequest = {
      reportId: idToUpdate,
    }

    const response: IFetchReportImageResponse = yield call(
      [ekgImportService, ekgImportService.fetchReportImage],
      fetchReportImageRequest
    )

    if (response.status === 200) {
      const imageObject = response.data.reportImage

      const loadedReportImageAttachment: IReportAttachment = {
        ...reportAttachmentToUpdate,
        isLoading: false,
        isUnavailable: !imageObject || imageObject.blobData === null,
        data: imageObject?.blobData || '',
      }

      reportAttachments[reportAttachmentToUpdateIndex] = loadedReportImageAttachment
      yield put(saveReportAttachments(reportAttachments))
    } else {
      response.failure = true
      if (response.messages?.length) {
        const index = response.messages.findIndex((x) => x.status === response.status)
        response.responseMessage = response.messages[index].message
      } else {
        response.responseMessage =
          'There was an error processing your request. Please contact customer support for further assistance.'
      }
      yield put(handleApiError(response))
    }
  } catch (e: any) {
    const cause = e.message ? e.message : 'unknown'
    const errorResponse = {
      status: null,
      messages: [
        {
          status: null,
          display: [
            { id: 3, name: 'Alert' },
            { id: 1, name: 'Log' },
          ],
          message: `An error occurred during EKG import. Cause: ${cause}`,
        },
      ],
    }
    yield put(handleApiError(errorResponse))
  }
}

export function* getReportAttachmentImageSaga() {
  yield takeEvery([EKGImportConstants.GET_REPORT_ATTACHMENT_IMAGE], getReportAttachmentImage)
}
